/**
 * `<Panel>` — the CONTENT renderer. Renders a panel's elements and nothing else.
 *
 * It deliberately owns no presentation behaviour: no portal, no scrim, no focus
 * trap, no Escape handling, no queueing. Those belong to a presenter
 * (`<PanelHost>` for modals). Dropping `<Panel>` straight into a page IS the
 * inline presentation — that is the whole point of separating the two.
 *
 * Keeping them separate is also an accessibility guard: a modal must trap focus
 * and a heading-labelled inline region must NOT. If one component did both, the
 * inline case would inherit dialog behaviour and quietly break keyboard
 * navigation on the page around it. Here the semantics are chosen by the caller
 * — the presenter passes `role="dialog"` / `aria-modal`, and the default is a
 * labelled `region`.
 *
 * The renderer never sets `innerHTML`, never fetches, and never navigates.
 */
import {
  forwardRef,
  useId,
  useLayoutEffect,
  useRef,
  type HTMLAttributes,
  type ReactNode,
} from 'react';

import { parsePanelAction } from './action';
import { ensurePanelStyles } from './styles';
import { panelThemeVars } from './theme';
import { substitutePlaceholders } from './text';
import type {
  PanelAction,
  PanelConditionalTextElement,
  PanelContent,
  PanelButtonElement,
  PanelElement,
  PanelImageRef,
  PanelItem,
  PanelRenderContext,
  PanelTextElement,
  PanelTheme,
} from './types';

export interface PanelProps extends Omit<HTMLAttributes<HTMLDivElement>, 'title' | 'children'> {
  definition: PanelContent;
  context?: PanelRenderContext;
  /**
   * Called when the user activates a button whose action parses. A button with
   * an unparseable action is rendered disabled and can never fire this.
   */
  onAction?: (action: PanelAction) => void;
  /** Width preset. Only meaningful when a presenter constrains the panel. */
  size?: 'small' | 'medium' | 'large';
  /**
   * When provided, renders a close affordance in the corner. Presenters pass
   * this; inline panels usually don't have one.
   */
  onDismiss?: () => void;
  /** Accessible name for the close affordance. */
  dismissLabel?: string;
  /**
   * Which heading element the title renders as. Default `2`.
   *
   * Matters for INLINE panels: the panel joins the host page's heading outline,
   * and a hard-coded `<h2>` inside a section whose own heading is an `h3` would
   * break the document hierarchy. Modal panels can leave the default — a dialog
   * is its own context. Visual size is unaffected (the title is styled by
   * class, not by element).
   */
  headingLevel?: 2 | 3 | 4;
  /**
   * An authored theme applied to this panel.
   *
   * Useful for the INLINE presentation, where the panel is an ordinary element in
   * the page and the variables it reads are its own. The MODAL path takes the
   * theme on `<PanelHost>` instead — the scrim reads two of the variables and is
   * the panel's parent, and the presenter portals out of the host's subtree.
   *
   * Merged UNDER any `style` the caller passes, so an explicit style wins.
   */
  theme?: PanelTheme;
}

/**
 * Resolve an image reference to a URL.
 *
 * Some paths hand us a reference the host must resolve (an arche asset id; a
 * storage key needing a presigned URL) — that is what `context.resolveImage` is
 * for, and it is the only mechanism enabled by default.
 *
 * Other paths have ALREADY resolved it: the bridge asset presigner rewrites
 * storage keys to signed URLs in place on the way out of `get_config`, so by
 * render time the field holds an `https:` URL. Those consumers opt in with
 * `context.allowResolvedUrls` — see the doc on that field for why accepting such
 * URLs unconditionally would make this renderer the weakest link in the
 * write-time root lock.
 *
 * Anything unresolved renders nothing: a missing image must never break the rest
 * of the panel, and must never fall back to "probably a URL".
 */
function resolveImageUrl(
  ref: PanelImageRef,
  context: PanelRenderContext | undefined,
): string | undefined {
  const resolved = context?.resolveImage?.(ref);
  if (resolved) return resolved;
  if (!context?.allowResolvedUrls || ref.kind !== 'storageKey') return undefined;
  try {
    return new URL(ref.imageKey).protocol === 'https:' ? ref.imageKey : undefined;
  } catch {
    return undefined;
  }
}

/** Overridable via `context.formatAmount` — see the note on that field. */
function defaultFormatAmount(amount: number): string {
  return `×${amount}`;
}

/**
 * Look up a named item list.
 *
 * The own-property check is load-bearing, not defensive habit: the slot grammar
 * permits `constructor`, `toString` and friends, so a plain `itemsBySlot[slot]`
 * would reach `Object.prototype` and hand back a function that the render path
 * below would then call `.map` on — a crash from an authored string. Mirrors the
 * same guard in `config-structured-schemas.ts`. The `Array.isArray` check makes
 * the failure mode "renders empty" for any other surprise, which is the same
 * degrade as an unfilled slot.
 */
function resolveSlot(
  context: PanelRenderContext | undefined,
  slot: string,
): PanelItem[] | undefined {
  const bySlot = context?.itemsBySlot;
  if (!bySlot || !Object.prototype.hasOwnProperty.call(bySlot, slot)) return undefined;
  const list = bySlot[slot];
  return Array.isArray(list) ? list : undefined;
}

/**
 * Is a named render-context value present AND truthy?
 *
 * The own-property check is load-bearing, not defensive habit — the same guard,
 * and the same reason, as {@link resolveSlot}. `showIf` accepts any identifier,
 * which includes `constructor`, `toString` and `valueOf`; a plain
 * `values[showIf]` reaches `Object.prototype`, finds a function, and reports it
 * as truthy. The line would then draw on EVERY panel — an unconditional "New
 * high score!", which is precisely the false claim this element kind exists to
 * prevent. Inherited properties are not values the caller passed.
 */
function hasTruthyValue(context: PanelRenderContext | undefined, name: string): boolean {
  const values = context?.values;
  if (!values || !Object.prototype.hasOwnProperty.call(values, name)) return false;
  return Boolean(values[name]);
}

function ImageElement({
  src,
  alt,
  maxHeight,
}: {
  src: string | undefined;
  alt: string | undefined;
  maxHeight: number | undefined;
}): ReactNode {
  if (!src) return null;
  return (
    <img
      className="fias-panel__image"
      src={src}
      // Decorative art with no author-supplied alt is hidden from assistive
      // tech (empty alt) rather than announced as a filename.
      alt={alt ?? ''}
      style={maxHeight != null ? { maxHeight: `${maxHeight}px` } : undefined}
    />
  );
}

/** The shared body of `text` and `conditionalText` — the two differ only in whether they draw. */
function TextElement({
  element,
  context,
}: {
  element: PanelTextElement | PanelConditionalTextElement;
  context: PanelRenderContext | undefined;
}): ReactNode {
  const style = element.style ?? 'body';
  const align = element.align ?? 'center';
  return (
    <p className={`fias-panel__text fias-panel__text--${style} fias-panel__text--${align}`}>
      {substitutePlaceholders(element.text, context?.values)}
    </p>
  );
}

function renderElement(
  element: PanelElement,
  key: string,
  context: PanelRenderContext | undefined,
): ReactNode {
  switch (element.kind) {
    case 'text':
      return <TextElement key={key} element={element} context={context} />;
    case 'conditionalText':
      // Plain truthiness on a NAMED value — no operators, by design (see
      // `panelConditionalTextElementSchema`). `0`, `''`, `false` and an absent
      // value all hide the line, which is what lets a game pass the counters it
      // already has without computing a second set of booleans.
      return hasTruthyValue(context, element.showIf) ? (
        <TextElement key={key} element={element} context={context} />
      ) : null;
    case 'image':
      return (
        <ImageElement
          key={key}
          src={resolveImageUrl({ kind: 'storageKey', imageKey: element.imageKey }, context)}
          alt={element.alt}
          maxHeight={element.maxHeight}
        />
      );
    case 'assetImage':
      return (
        <ImageElement
          key={key}
          src={resolveImageUrl({ kind: 'asset', assetId: element.assetId }, context)}
          alt={element.alt}
          maxHeight={element.maxHeight}
        />
      );
    case 'items':
    case 'slottedItems': {
      // `slottedItems` draws a NAMED list; `items` draws the default one. A slot
      // the caller never filled is NOT an error — it takes the same empty path as
      // an empty default list, so a panel authored for four reward tiers degrades
      // cleanly for a player who earned one. It must never fall back to the
      // default list: that would show rewards the player did not earn.
      const items =
        (element.kind === 'slottedItems' ? resolveSlot(context, element.slot) : context?.items) ??
        [];
      if (items.length === 0) {
        return element.emptyText ? (
          <p key={key} className="fias-panel__items-empty">
            {element.emptyText}
          </p>
        ) : null;
      }
      const showIcons = element.showIcons ?? true;
      const iconSize = element.iconSize ?? 'medium';
      return (
        <ul key={key} className="fias-panel__items">
          {items.map((item, index) => (
            <li className="fias-panel__item" key={`${item.label}-${index}`}>
              {showIcons && item.icon ? (
                <span className="fias-panel__item-icon" aria-hidden>
                  {item.icon}
                </span>
              ) : null}
              {showIcons && !item.icon && item.imageUrl ? (
                <img
                  className={`fias-panel__item-icon fias-panel__item-icon--${iconSize}`}
                  src={item.imageUrl}
                  alt=""
                />
              ) : null}
              <span>{item.label}</span>
              {item.amount != null ? (
                <span className="fias-panel__item-amount">
                  {(context?.formatAmount ?? defaultFormatAmount)(item.amount)}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      );
    }
    case 'spacer':
      return (
        <div key={key} className={`fias-panel__spacer--${element.size ?? 'medium'}`} aria-hidden />
      );
    default:
      // Unknown kinds are SKIPPED, never thrown on: a shipped renderer may be
      // older than the definition it is handed. The write validator is the
      // place that rejects unknown kinds.
      return null;
  }
}

/**
 * Consecutive buttons render as one row, and a text element with `joinNext`
 * pulls the element after it onto its own row. Grouping is visual only — it never
 * reorders elements, so a button between two texts still renders between them.
 *
 * Every group is TAGGED (`group`), rather than distinguished by shape. The tag is
 * deliberately not called `kind`: these are groups of elements, and a `kind` here
 * would read as an element kind and collide with one the day a `row` element is
 * ratified. The button group stays typed as `PanelButtonElement[]` so the render
 * path needs no re-narrowing — the array type carries the guarantee that only
 * buttons are in it.
 */
type PanelElementGroup =
  | { group: 'element'; element: PanelElement }
  | { group: 'buttons'; buttons: PanelButtonElement[] }
  | { group: 'row'; elements: PanelElement[] };

/** Does this element ask to share a line with the one after it? */
function joinsNext(element: PanelElement): boolean {
  return (
    (element.kind === 'text' || element.kind === 'conditionalText') && element.joinNext === true
  );
}

function groupElements(elements: PanelElement[]): PanelElementGroup[] {
  const groups: PanelElementGroup[] = [];
  // The row currently accepting elements, or null. A run of consecutive
  // `joinNext` texts collects into ONE row (`Score: 20 Best: 120` on a line),
  // which falls out of keeping the row open while each element also joins.
  let openRow: PanelElement[] | null = null;

  for (const element of elements) {
    if (openRow && element.kind !== 'button') {
      openRow.push(element);
      if (!joinsNext(element)) openRow = null;
      continue;
    }
    // A button never gets absorbed into a text row: buttons are the panel's
    // interactive affordances and keeping them in their own row is what makes
    // them consistently placed and comfortably sized to hit. A trailing
    // `joinNext` before a button simply renders as its own line.
    openRow = null;

    const last = groups[groups.length - 1];
    if (element.kind === 'button') {
      if (last?.group === 'buttons') last.buttons.push(element);
      else groups.push({ group: 'buttons', buttons: [element] });
      continue;
    }
    if (joinsNext(element)) {
      openRow = [element];
      groups.push({ group: 'row', elements: openRow });
      continue;
    }
    groups.push({ group: 'element', element });
  }
  return groups;
}

export const Panel = forwardRef<HTMLDivElement, PanelProps>(function Panel(
  {
    definition,
    context,
    onAction,
    size,
    onDismiss,
    dismissLabel = 'Close',
    headingLevel = 2,
    theme,
    className,
    style,
    role = 'region',
    ...rest
  },
  forwardedRef,
) {
  const Heading = `h${headingLevel}` as const;
  const titleId = useId();
  const rootRef = useRef<HTMLDivElement | null>(null);

  // Layout effect, not a passive one: passive effects run AFTER paint, so the
  // first panel ever mounted in a document could flash one unstyled frame.
  // A layout effect runs after the DOM is committed (the ref is attached, so we
  // can reach the panel's OWN document — inside a plugin iframe that is not the
  // top document) but before the browser paints. Not `useInsertionEffect`
  // because that fires before refs are attached, which would lose exactly the
  // ownerDocument targeting this injection exists for.
  useLayoutEffect(() => {
    ensurePanelStyles(rootRef.current?.ownerDocument);
  }, []);

  const setRef = (node: HTMLDivElement | null) => {
    rootRef.current = node;
    if (typeof forwardedRef === 'function') forwardedRef(node);
    else if (forwardedRef) forwardedRef.current = node;
  };

  const classes = ['fias-panel'];
  if (size) classes.push(`fias-panel--${size}`);
  if (className) classes.push(className);

  return (
    <div
      {...rest}
      ref={setRef}
      className={classes.join(' ')}
      style={theme ? { ...panelThemeVars(theme), ...style } : style}
      role={role}
      aria-labelledby={titleId}
    >
      {onDismiss ? (
        <button type="button" className="fias-panel__close" onClick={onDismiss}>
          <span aria-hidden>×</span>
          <span className="fias-panel__sr-only">{dismissLabel}</span>
        </button>
      ) : null}

      <Heading className="fias-panel__title" id={titleId}>
        {definition.title}
      </Heading>

      {groupElements(definition.elements).map((group, index) => {
        if (group.group === 'element') return renderElement(group.element, `el-${index}`, context);

        if (group.group === 'row') {
          // A `conditionalText` inside a row that evaluates false renders nothing
          // and the row closes up around it — the remaining element keeps its
          // place rather than the whole row disappearing.
          return (
            <div className="fias-panel__row" key={`row-${index}`}>
              {group.elements.map((element, elementIndex) =>
                renderElement(element, `row-${index}-${elementIndex}`, context),
              )}
            </div>
          );
        }

        return (
          <div className="fias-panel__buttons" key={`btn-${index}`}>
            {group.buttons.map((button, buttonIndex) => {
              const parsed = parsePanelAction(button.action);
              return (
                <button
                  type="button"
                  key={`btn-${index}-${buttonIndex}`}
                  className={`fias-panel__button fias-panel__button--${button.variant ?? 'primary'}`}
                  // An action the grammar rejects makes the button inert rather
                  // than removing it: the layout the author intended is
                  // preserved, and the bad action cannot fire.
                  disabled={parsed === null}
                  onClick={parsed === null ? undefined : () => onAction?.(parsed)}
                >
                  {button.label}
                </button>
              );
            })}
          </div>
        );
      })}
    </div>
  );
});
